import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SettingsIcon from '@mui/icons-material/Settings';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { SETTINGS_MODULE_GROUP_LABELS } from '@/config/settingsRegistry';
import { useActiveModule } from '@/hooks/useActiveModule';
import { localizeModuleLabel } from '@/i18n/navLabel';
import { SettingsCategoryCard } from './SettingsCategoryCard';
import { SettingsSearch } from './SettingsSearch';
import { useActiveSettingsModuleKey, useModuleSettingsSections } from './settingsAccess';

export function SettingsPage() {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const activeModule = useActiveModule();
  const scopeModule = useActiveSettingsModuleKey();
  const sections = useModuleSettingsSections();

  const moduleLabel =
    scopeModule === 'PLATFORM'
      ? SETTINGS_MODULE_GROUP_LABELS.PLATFORM
      : activeModule
        ? localizeModuleLabel(activeModule, t)
        : SETTINGS_MODULE_GROUP_LABELS.PLATFORM;

  useEffect(() => {
    const hash = location.hash.replace(/^#/, '');
    if (!hash) return;
    const target = document.getElementById(hash);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash, sections.length]);

  return (
    <div className="page-content w-full min-w-0">
      <PageHeader
        title={t('paths.settings', { defaultValue: 'Settings' })}
        description={`Configure ${moduleLabel} masters, organization and platform options.`}
        icon={SettingsIcon}
        actions={<SettingsSearch compact className="hidden sm:block" />}
      />

      {sections.length === 0 ? (
        <EmptyState
          title="No settings available"
          description={`There are no settings screens available for ${moduleLabel} with your current access.`}
        />
      ) : (
        <>
          <SettingsSearch className="mb-4 sm:hidden" />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {sections.map(({ category, masters }) => (
              <SettingsCategoryCard
                key={category.key}
                id={category.key}
                categoryKey={category.key}
                label={category.label}
                icon={category.icon}
                masters={masters}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
